import { rolesApi, type RoleDeleteRequest } from '@/api/roles';
import type {
    RoleAddRequest,
    RoleDetailsRequest,
    RolesRequest,
    RoleUpdateRequest,
} from '@/types/api/roles';
import type { Role } from '@/types/models';
import { createEventHook, createSharedComposable } from '@vueuse/core';
import {
    useDelete,
    useGet,
    usePost,
    usePut,
    useToaster,
} from '@foodics/ui-common';
import { computed, ref } from 'vue';
import { useLocale } from '@/composables/locale';
import { parseError } from '@/utils/errors';

export type RolesFilters = {
    type?: 'custom' | 'default' | 'all';
};

export const { trigger: triggerRoleUpdated, on: onRoleUpdated } =
    createEventHook<Role>();

export const { trigger: triggerRoleDeleted, on: onRoleDeleted } =
    createEventHook<string>();

export const useRoles = createSharedComposable(() => {
    const { addToast } = useToaster();
    const { t } = useLocale();

    const appliedFilters = ref<RolesFilters>({});
    function setFilter<T extends keyof RolesFilters>(
        key: T,
        value: RolesFilters[T],
    ) {
        if (value) appliedFilters.value[key] = value;
        else delete appliedFilters.value[key];
    }

    const { data: rolesData, isLoading } = useGet<RolesRequest['response']>(
        rolesApi.list,
        {
            params: { include: 'users' },
        },
    );

    onRoleUpdated((role) => {
        if (!rolesData.value) return;

        const roleToUpdateIdx = (rolesData.value.data || []).findIndex(
            (r) => r.id === role.id,
        );

        if (roleToUpdateIdx >= 0) {
            rolesData.value.data[roleToUpdateIdx] = role;
        }
    });

    onRoleDeleted((id) => {
        if (!rolesData.value) return;

        const roleToDeleteIdx = (rolesData.value.data || []).findIndex(
            (r) => r.id === id,
        );

        if (roleToDeleteIdx >= 0) {
            rolesData.value.data.splice(roleToDeleteIdx, 1);
        }
    });

    const filteredRoles = computed(() => {
        if (!rolesData.value) return [];

        const filteredData = rolesData.value.data.filter((role) => {
            if (appliedFilters.value.type === 'custom') {
                return role.is_custom;
            }

            if (appliedFilters.value.type === 'default') {
                return !role.is_custom;
            }

            return true;
        });

        // Sort roles by the count of members in each role (the higher count first)
        const sorted = [...filteredData].sort((a, b) => {
            return b.users.length - a.users.length;
        });

        return sorted;
    });

    function findRole(id: string) {
        return (rolesData.value?.data || []).find((r) => r.id === id);
    }

    const { execute: executeAdd, onData: onRoleAdded } = usePost<
        RoleAddRequest['payload'],
        RoleAddRequest['response']
    >(rolesApi.add);

    async function addRole(role: { name: string; permissions: string[] }) {
        try {
            const { data, error } = await executeAdd({
                name: role.name,
                permissions: role.permissions,
            });

            if (error) throw error;
            if (!data)
                throw new Error('Something went wrong while adding the role');

            addToast({
                title: t('settings.role_toasts.role_created', {
                    name: role.name,
                }),
                variant: 'success',
            });

            if (rolesData.value) {
                rolesData.value.data.push({
                    id: data.data.id,
                    name: data.data.name,
                    is_custom: data.data.is_custom,
                    created_at: data.data.created_at,
                    updated_at: data.data.updated_at,
                    users: [],
                });
            }

            return data.data;
        } catch (error) {
            const message = parseError(
                error,
                t('settings.role_toasts.role_add_error'),
            );

            addToast({
                title: message,
                variant: 'error',
            });
        }
    }

    return {
        roles: filteredRoles,
        isLoading,
        setFilter,
        findRole,
        addRole,
        onRoleAdded,
    };
});

export const useRole = createSharedComposable((id: string) => {
    const { addToast } = useToaster();
    const { t } = useLocale();

    const {
        data: roleData,
        isLoading,
        error,
    } = useGet<RoleDetailsRequest['response']>(rolesApi.details(id));

    const {
        execute: executeUpdate,
        isLoading: isUpdating,
        error: updateError,
        onData: onRoleUpdated,
    } = usePut<RoleUpdateRequest['payload'], RoleUpdateRequest['response']>(
        rolesApi.update(id),
    );

    async function update(payload: RoleUpdateRequest['payload']) {
        try {
            const { data, error } = await executeUpdate(payload);

            if (error) throw error;
            if (!data?.data)
                throw new Error(t('generic.common_terms.something_went_wrong'));

            addToast({
                title: t('settings.role_toasts.role_updated'),
                variant: 'success',
            });

            if (roleData.value) {
                roleData.value.data = data.data;
            }

            triggerRoleUpdated(data.data);
        } catch (error) {
            addToast({
                title: parseError(
                    error,
                    t('settings.role_toasts.role_update_error'),
                ),
                variant: 'error',
            });
        }
    }

    return {
        role: computed(() => roleData.value?.data),
        isLoading,
        error,
        update,
        isUpdating,
        onRoleUpdated,
        updateError,
    };
});

export function useDeleteRole(id: string, name?: string) {
    const { addToast } = useToaster();
    const { t } = useLocale();

    const { execute: executeDelete, isLoading } = useDelete<
        RoleDeleteRequest['response']
    >(rolesApi.delete(id));

    async function deleteRole() {
        try {
            const { error } = await executeDelete();

            if (error) throw error;

            addToast({
                title: name
                    ? t('settings.role_toasts.role_deleted', { name })
                    : t('settings.role_toasts.role_deleted_fallback'),
                variant: 'success',
            });

            triggerRoleDeleted(id);
        } catch (error) {
            addToast({
                title: parseError(
                    error,
                    t('settings.role_toasts.role_delete_error'),
                ),
                variant: 'error',
            });
        }
    }

    return {
        deleteRole,
        isDeleting: isLoading,
    };
}

export function isAdminRole(
    role: Partial<Role> & Pick<Role, 'name' | 'is_custom'>,
) {
    return role.name === 'Admin' && !role.is_custom;
}
